import { TapLevel } from '@/domain/types';
import { TAP_SESSION_DURATION_SECONDS } from '@/domain/constants';
import { useEffect, useState } from 'react';

interface HUDProps {
  endTime: Date | null;
  points: number;
  tapDelta: number;
  isActive: boolean;
  currentLevel: TapLevel;
}

export function HUD({ endTime, points, tapDelta, isActive, currentLevel }: HUDProps) {
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    if (!isActive || !endTime) {
      setTimeRemaining(0);
      return;
    }

    const update = () => {
      const remaining = Math.max(0, (endTime.getTime() - Date.now()) / 1000);
      // HUD displays tenths; avoid extra rerenders when the displayed value didn't change.
      setTimeRemaining((prev) => {
        const prevDisplayed = Math.round(prev * 10);
        const nextDisplayed = Math.round(remaining * 10);
        return prevDisplayed === nextDisplayed ? prev : remaining;
      });
    };

    update();
    const interval = window.setInterval(update, 100);
    return () => window.clearInterval(interval);
  }, [isActive, endTime]);

  // Format time with one decimal: "11.9s" -> "11.9"
  const formatTime = (seconds: number): string => {
    const s = Math.max(0, seconds);
    return s.toFixed(1);
  };

  // Timer progress (1 = full, 0 = empty)
  const timerProgress = isActive ? timeRemaining / TAP_SESSION_DURATION_SECONDS : 1;

  // Timer color based on remaining time percentage (spec: >30% neutral, <30% orange, <10% red)
  const getTimerColor = () => {
    if (!isActive) return { text: 'text-gray-400', bar: '#4a5568' };
    if (timerProgress < 0.1) return { text: 'text-red-500', bar: '#ef4444' };
    if (timerProgress < 0.3) return { text: 'text-accent-orange', bar: '#ff6a00' };
    return { text: 'text-glow-cyan', bar: '#00ffff' };
  };

  const timerColors = getTimerColor();

  // Level indicator colors
  const getLevelStyles = (level: TapLevel) => {
    switch (level) {
      case 3:
        return { bg: 'from-accent-orange to-orange-600', text: 'text-white', glow: 'glow-orange' };
      case 2:
        return { bg: 'from-glow-cyan to-primary-500', text: 'text-white', glow: 'glow-cyan-sm' };
      default:
        return { bg: 'from-gray-500 to-gray-600', text: 'text-gray-200', glow: '' };
    }
  };

  const levelStyles = getLevelStyles(currentLevel);

  // Gradient border color based on state
  const timerBorderColor = isActive ? timerColors.bar : 'rgba(100, 100, 100, 0.5)';

  // Dynamic font size for points based on digit count
  const getPointsFontSize = (pts: number): string => {
    if (pts >= 1000000) return 'text-xs';   // 1,000,000+ → 12px
    if (pts >= 100000) return 'text-sm';    // 100,000+ → 14px
    if (pts >= 10000) return 'text-base';   // 10,000+ → 16px
    return 'text-lg';                        // < 10,000 → 18px
  };

  const pointsFontSize = getPointsFontSize(points);

  return (
    <div className="flex items-start justify-between">
      {/* Timer section (left) */}
      <div className="flex flex-col gap-1">
        <div
          className="relative flex min-w-[7rem] items-center justify-center px-3 py-2"
          style={{
            background: 'rgba(10, 10, 20, 0.8)',
          }}
        >
          {/* Top gradient line */}
          <div
            className="absolute left-0 right-0 top-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${timerBorderColor} 30%, ${timerBorderColor} 70%, transparent 100%)`,
            }}
          />
          {/* Bottom gradient line */}
          <div
            className="absolute bottom-0 left-0 right-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${timerBorderColor} 30%, ${timerBorderColor} 70%, transparent 100%)`,
            }}
          />
          <span className={`font-mono text-lg font-bold transition-colors duration-300 ${timerColors.text} ${isActive && timerProgress >= 0.3 ? 'glow-cyan-sm' : ''}`}>
            {isActive ? formatTime(timeRemaining) : '0'}
          </span>
        </div>

        {/* Progress bar below timer - only shown when active */}
        {isActive && (
          <div
            className="h-1.5 w-full overflow-hidden rounded-full"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          >
            <div
              className="h-full rounded-full transition-all duration-200"
              style={{
                width: `${timerProgress * 100}%`,
                background: timerColors.bar,
                boxShadow: `0 0 8px ${timerColors.bar}80`,
              }}
            />
          </div>
        )}
      </div>

      {/* Level indicator (center) */}
      {isActive && (
        <div
          className={`relative px-3 py-1.5 ${levelStyles.glow}`}
          style={{
            background: 'rgba(10, 10, 20, 0.8)',
          }}
        >
          {/* Top gradient line */}
          <div
            className="absolute left-0 right-0 top-0 h-px"
            style={{
              background: currentLevel === 3
                ? 'linear-gradient(90deg, transparent 0%, rgba(255, 106, 0, 0.7) 30%, rgba(255, 106, 0, 0.7) 70%, transparent 100%)'
                : currentLevel === 2
                  ? 'linear-gradient(90deg, transparent 0%, rgba(0, 212, 255, 0.7) 30%, rgba(0, 212, 255, 0.7) 70%, transparent 100%)'
                  : 'linear-gradient(90deg, transparent 0%, rgba(100, 100, 100, 0.5) 30%, rgba(100, 100, 100, 0.5) 70%, transparent 100%)',
            }}
          />
          {/* Bottom gradient line */}
          <div
            className="absolute bottom-0 left-0 right-0 h-px"
            style={{
              background: currentLevel === 3
                ? 'linear-gradient(90deg, transparent 0%, rgba(255, 106, 0, 0.7) 30%, rgba(255, 106, 0, 0.7) 70%, transparent 100%)'
                : currentLevel === 2
                  ? 'linear-gradient(90deg, transparent 0%, rgba(0, 212, 255, 0.7) 30%, rgba(0, 212, 255, 0.7) 70%, transparent 100%)'
                  : 'linear-gradient(90deg, transparent 0%, rgba(100, 100, 100, 0.5) 30%, rgba(100, 100, 100, 0.5) 70%, transparent 100%)',
            }}
          />
          <span className={`text-sm font-bold ${levelStyles.text}`}>LVL {currentLevel}</span>
        </div>
      )}

      {/* Points section (right) - numbers pulse when points are added */}
      <div
        className="relative flex min-w-[7rem] items-center justify-end gap-2 px-3 py-2"
        style={{
          background: 'rgba(10, 10, 20, 0.8)',
        }}
      >
        {/* Top gradient line */}
        <div
          className="absolute left-0 right-0 top-0 h-px"
          style={{
            background: isActive
              ? 'linear-gradient(90deg, transparent 0%, rgba(255, 215, 0, 0.7) 30%, rgba(255, 215, 0, 0.7) 70%, transparent 100%)'
              : 'linear-gradient(90deg, transparent 0%, rgba(100, 100, 100, 0.5) 30%, rgba(100, 100, 100, 0.5) 70%, transparent 100%)',
          }}
        />
        {/* Bottom gradient line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: isActive
              ? 'linear-gradient(90deg, transparent 0%, rgba(255, 215, 0, 0.7) 30%, rgba(255, 215, 0, 0.7) 70%, transparent 100%)'
              : 'linear-gradient(90deg, transparent 0%, rgba(100, 100, 100, 0.5) 30%, rgba(100, 100, 100, 0.5) 70%, transparent 100%)',
          }}
        />
        <span className={`font-mono ${pointsFontSize} font-bold transition-all duration-150 origin-right ${
          isActive
            ? `text-accent-gold glow-gold-sm ${tapDelta > 0 ? 'glow-gold scale-110' : ''}`
            : 'text-gray-400'
        }`}>
          {points.toLocaleString()}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          className={isActive ? 'text-accent-gold' : 'text-gray-400'}
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  );
}
